/**
 * Analyze audio blob for silence detection.
 * Uses Web Audio API to compute amplitude, energy variance, and zero-crossing rate.
 *
 * @param {Blob} audioBlob - The audio blob to analyze
 * @returns {Promise<Object>} Analysis result with isSilent flag and audio metrics
 */
export async function analyzeAudioLevel(audioBlob) {
  return new Promise((resolve) => {
    try {
      if (audioBlob.size < 1000) {
        resolve({ isSilent: true, reason: 'file_too_small', size: audioBlob.size });
        return;
      }

      const audioContext = new ((typeof window !== 'undefined' && window.AudioContext) || (typeof window !== 'undefined' && window.webkitAudioContext))();
      const fileReader = new FileReader();

      const timeout = setTimeout(() => {
        resolve({ isSilent: true, reason: 'analysis_timeout' });
      }, 3000);

      fileReader.onload = async (e) => {
        try {
          clearTimeout(timeout);
          const arrayBuffer = e.target.result;

          if (!arrayBuffer || arrayBuffer.byteLength === 0) {
            resolve({ isSilent: true, reason: 'empty_buffer' });
            return;
          }

          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const audioData = audioBuffer.getChannelData(0);

          let sum = 0;
          let maxAbs = 0;
          let energyVariance = 0;
          let zeroCrossings = 0;
          const sampleStep = Math.max(1, Math.floor(audioData.length / 2000));
          const samples = [];

          for (let i = 0; i < audioData.length; i += sampleStep) {
            const abs = Math.abs(audioData[i]);
            sum += abs;
            if (abs > maxAbs) maxAbs = abs;
            samples.push(abs);

            if (i > 0 && ((audioData[i] >= 0) !== (audioData[i - sampleStep] >= 0))) {
              zeroCrossings++;
            }
          }

          const avgAmplitude = sum / samples.length;
          const peak = maxAbs;

          for (let i = 0; i < samples.length; i++) {
            energyVariance += Math.pow(samples[i] - avgAmplitude, 2);
          }
          energyVariance = energyVariance / samples.length;

          const zcr = zeroCrossings / samples.length;

          const hasVariation = energyVariance > 0.00005;
          const hasSpeechZCR = zcr > 0.005 && zcr < 0.8;
          const hasReasonableAmplitude = avgAmplitude > 0.002 && avgAmplitude < 0.5;
          const hasReasonablePeak = peak > 0.01 && peak < 1.0;

          const speechIndicators = [hasVariation, hasSpeechZCR, hasReasonableAmplitude, hasReasonablePeak].filter(Boolean).length;
          const hasSignificantAmplitude = avgAmplitude > 0.01 || peak > 0.05;

          const isSilent = !(speechIndicators >= 2 || hasSignificantAmplitude);

          const analysis = {
            avgAmplitude,
            peak,
            energyVariance,
            zcr,
            duration: audioBuffer.duration,
            sampleRate: audioBuffer.sampleRate,
            isSilent,
            reason: isSilent ? 'no_speech_detected' : 'speech_detected',
            speechCharacteristics: {
              hasVariation,
              hasSpeechZCR,
              hasReasonableAmplitude,
              hasReasonablePeak
            }
          };

          resolve(analysis);

        } catch (error) {
          clearTimeout(timeout);
          console.error('Audio analysis failed:', error);
          resolve({ isSilent: false, error: error.message, reason: 'analysis_error' });
        }
      };

      fileReader.onerror = () => {
        clearTimeout(timeout);
        console.error('FileReader error');
        resolve({ isSilent: false, reason: 'file_read_error' });
      };

      fileReader.readAsArrayBuffer(audioBlob);

    } catch (error) {
      console.error('Audio analysis setup failed:', error);
      resolve({ isSilent: false, error: error.message, reason: 'setup_error' });
    }
  });
}
